import {
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';

/**
 * Makes any block element horizontally resizable via a drag handle on its
 * right edge. Width is constrained between {@code minWidth} and
 * {@code maxWidth} and persisted to {@code localStorage} under
 * {@code storageKey} so it survives page reloads.
 *
 * <h2>Usage</h2>
 *
 * <pre>
 *   &lt;aside class="sp-workflow-sidebar"
 *          spResizable
 *          [minWidth]="220"
 *          [maxWidth]="520"
 *          [defaultWidth]="280"
 *          storageKey="sp-workflow-sidebar-width"&gt;
 *     ...
 *   &lt;/aside&gt;
 * </pre>
 *
 * <h2>Interaction</h2>
 * <ul>
 *   <li>Hover near the right edge — the handle highlights in the brand
 *       navy and the cursor turns to {@code col-resize}.</li>
 *   <li>Click and drag horizontally to resize. Body cursor is locked to
 *       {@code col-resize} during the drag so the pointer doesn't change
 *       when crossing element boundaries.</li>
 *   <li>Double-click the handle to reset to {@code defaultWidth}.</li>
 *   <li>Final width persists to {@code localStorage} on mouse-up.</li>
 * </ul>
 *
 * <h2>Why a directive (not a component)</h2>
 *
 * <p>The host element keeps all of its existing classes, content and
 * layout. The directive only injects the handle element and manages
 * width; no template or projection rewrite needed at call sites.
 */
@Directive({
  selector: '[spResizable]',
  standalone: true
})
export class SpResizableDirective implements OnInit, OnDestroy {

  @Input() minWidth = 200;
  @Input() maxWidth = 520;
  @Input() defaultWidth = 280;
  @Input() storageKey = 'sp-resizable-width';

  @HostBinding('style.position') position = 'relative';

  private handle!: HTMLElement;
  private resizing = false;
  private startX = 0;
  private startWidth = 0;

  private unlistenMove?: () => void;
  private unlistenUp?: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private r: Renderer2
  ) {}

  ngOnInit(): void {
    // Restore previous width or fall back to default
    const saved = parseInt(localStorage.getItem(this.storageKey) || '', 10);
    const initial = (Number.isFinite(saved) && saved >= this.minWidth && saved <= this.maxWidth)
      ? saved
      : this.defaultWidth;
    this.r.setStyle(this.el.nativeElement, 'width', initial + 'px');
    this.r.setStyle(this.el.nativeElement, 'flex-shrink', '0');

    this.createHandle();
  }

  ngOnDestroy(): void {
    this.endResize();
  }

  // ─── Handle creation + lifecycle ─────────────────────────────────────

  private createHandle(): void {
    this.handle = this.r.createElement('div');
    this.r.addClass(this.handle, 'sp-resize-handle');

    const s = (prop: string, val: string) => this.r.setStyle(this.handle, prop, val);
    s('position',   'absolute');
    s('top',        '0');
    s('right',      '-3px');
    s('bottom',     '0');
    s('width',      '6px');
    s('cursor',     'col-resize');
    s('z-index',    '20');
    s('background', 'transparent');
    s('transition', 'background 120ms ease');

    this.r.appendChild(this.el.nativeElement, this.handle);

    // Hover affordance
    this.r.listen(this.handle, 'mouseenter', () => {
      if (!this.resizing) {
        this.r.setStyle(this.handle, 'background', 'rgba(36, 64, 98, 0.20)');
      }
    });
    this.r.listen(this.handle, 'mouseleave', () => {
      if (!this.resizing) {
        this.r.setStyle(this.handle, 'background', 'transparent');
      }
    });

    // Start drag
    this.r.listen(this.handle, 'mousedown', (ev: MouseEvent) => this.startResize(ev));

    // Double-click resets to default
    this.r.listen(this.handle, 'dblclick', () => {
      this.r.setStyle(this.el.nativeElement, 'width', this.defaultWidth + 'px');
      localStorage.setItem(this.storageKey, String(this.defaultWidth));
    });
  }

  // ─── Drag handlers ───────────────────────────────────────────────────

  private startResize(ev: MouseEvent): void {
    ev.preventDefault();
    this.resizing = true;
    this.startX = ev.clientX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    this.r.setStyle(this.handle, 'background', 'rgba(36, 64, 98, 0.40)');
    this.r.setStyle(document.body, 'cursor', 'col-resize');
    this.r.setStyle(document.body, 'user-select', 'none');

    this.unlistenMove = this.r.listen('document', 'mousemove', (e: MouseEvent) => this.onResize(e));
    this.unlistenUp   = this.r.listen('document', 'mouseup',   () => this.endResize());
  }

  private onResize(ev: MouseEvent): void {
    if (!this.resizing) return;
    const delta = ev.clientX - this.startX;
    const candidate = this.startWidth + delta;
    const constrained = Math.min(Math.max(candidate, this.minWidth), this.maxWidth);
    this.r.setStyle(this.el.nativeElement, 'width', constrained + 'px');
  }

  private endResize(): void {
    if (!this.resizing) return;
    this.resizing = false;

    this.r.setStyle(this.handle, 'background', 'transparent');
    this.r.removeStyle(document.body, 'cursor');
    this.r.removeStyle(document.body, 'user-select');

    if (this.unlistenMove) { this.unlistenMove(); this.unlistenMove = undefined; }
    if (this.unlistenUp)   { this.unlistenUp();   this.unlistenUp   = undefined; }

    const finalWidth = this.el.nativeElement.offsetWidth;
    localStorage.setItem(this.storageKey, String(finalWidth));
  }
}